import React from "react";
import { observer } from "mobx-react";
import { Button, Group, Modal, Stack, Title, Text, Box } from "@mantine/core";
import { Cubby } from "../../stores/Cubby";
import { darkStyles, JsonView, collapseAllNested } from "react-json-view-lite";
import { makeChannelDisplayName } from "../../helpers/channels";
import {
  IconAbacus,
  IconBoltFilled,
  IconExternalLink,
} from "@tabler/icons-react";
import { TextWithInfo } from "../modals/textWithInfo";
import { useMediaQuery } from "@mantine/hooks";
import { cleanNullsAndSort, removeRawData } from "../../helpers/sort";
import ChannelKeyData from "./channelKeyData";
import { TextWithInfoAndIcon } from "./textWithInfoAndIcon";
import { ARTICLES } from "../../docs/docsIndex";

const renderTransactionLink = (transactionId: string, network: string) => {
  if (!transactionId) return null;
  const baseUrl =
    network === "testnet"
      ? "https://mempool.space/testnet/tx"
      : "https://mempool.space/tx";
  const url = `${baseUrl}/${transactionId}`;
  return (
    <Group justify="flex-end">
      <Button
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        variant="default"
        style={{ maxWidth: "60%" }}
        color="blue"
        radius="md"
        rightSection={<IconExternalLink />}
      >
        Transaction
      </Button>
    </Group>
  );
};

const ChannelInfo = observer(({ cubby }: { cubby: Cubby }) => {
  const channel = cubby.currentlyFocusedChannelDataForChannelInfo;

  const handleClose = () => {
    cubby.setCurrentlyFocusedChannelTransactionIdForChannelInfo(null);
  };

  const isMobile = useMediaQuery("(max-width: 768px)");
  const modalWidth = isMobile ? "95%" : "70%";

  return (
    <>
      {cubby.currentlyFocusedChannelTransactionIdForChannelInfo && (
        <Modal
          opened={!!cubby.currentlyFocusedChannelTransactionIdForChannelInfo}
          onClose={handleClose}
          size={modalWidth}
          title={
            <Box>
              <Group gap="xs" justify="flex-start">
                <IconBoltFilled />
                <Title ml="-.25rem" order={4}>
                  {makeChannelDisplayName(channel)}
                </Title>
              </Group>
              <Text size="xs" c="dimmed">
                Lightning Channel
              </Text>
            </Box>
          }
        >
          <Stack>
            {/* Render key data using ChannelKeyData component */}
            {channel && <ChannelKeyData channel={channel} cubby={cubby} />}

            {channel &&
              channel.channel_point &&
              renderTransactionLink(channel.channel_point, cubby.cubbyNetwork)}

            {channel && (
              <>
                <TextWithInfoAndIcon
                  text="Advanced Channel Data"
                  articleSlug={ARTICLES.CHANNEL_INFO.slug}
                  icon={<IconAbacus size={24} />}
                  cubby={cubby}
                  textSize="lg"
                />

                <JsonView
                  data={removeRawData(cleanNullsAndSort(channel))}
                  shouldExpandNode={collapseAllNested}
                  style={darkStyles}
                />
              </>
            )}
          </Stack>
        </Modal>
      )}
    </>
  );
});

export default ChannelInfo;
